import {
  LineChart,
  Line,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";
import dayjs from "dayjs";
import { Box, useTheme } from "@mui/material";

import React, { useMemo } from "react";
import InfoBox from "./InfoBox";
import ChartTooltip from "./ChartTooltip";
import ChartCard from "./ChartCard";
import { JourneyAnchors } from "../../../constants";
import { HighlightItemInfo } from "../components/HighlightItem";
import { NoActivityExerciseMessageBox } from "./NoActivityExerciseMessageBox";
interface ExerciseChartInfo {
  date: string;
  reps: {
    min: number;
    max: number;
  };
}

interface RepsChartProps {
  volumes: ExerciseChartInfo[];
  highlight?: HighlightItemInfo;
}

const RepsChart: React.FC<RepsChartProps> = ({ volumes, highlight }) => {
  const theme = useTheme();

  const chartData = useMemo(
    () =>
      volumes
        .map((v) => ({
          x: dayjs(v.date).toDate(),
          min: v.reps.min,
          max: v.reps.max,
          originalMin: v.reps.min,
          originalMax: v.reps.max,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime()),
    [volumes],
  );

  const [chartMin, chartMax] = useMemo(() => {
    const allMins = chartData.map((d) => d.min);
    const allMaxs = chartData.map((d) => d.max);

    const minValue = Math.max(Math.min(...allMins), 0);
    const maxValue = Math.max(...allMaxs, 0);

    const padding = 0.1 * (maxValue - minValue || 10);
    const adjustedMin = Math.max(Math.floor((minValue - padding) / 5) * 5, 0);
    const adjustedMax = Math.ceil((maxValue + padding) / 5) * 5;

    return [adjustedMin, adjustedMax];
  }, [chartData]);

  const chartTicks = useMemo(() => {
    if (chartData.length === 0) return [0];
    const tickCount = 7;
    const ticks = new Set<number>();
    for (let i = 1; i <= tickCount; i++) {
      const value =
        Math.round(
          ((Math.pow(i, 2) / Math.pow(tickCount, 2)) * (chartMax - chartMin) +
            chartMin) /
            5,
        ) * 5;
      ticks.add(Math.max(value, 0));
    }
    return Array.from(ticks).sort((a, b) => a - b);
  }, [chartMin, chartMax, chartData]);
  return (
    <ChartCard
      title="Reps Overview"
      classes={{ card: JourneyAnchors.REPS_CHART }}
      highlight={highlight}
      info={`See the minimum and maximum number of reps performed during each training day.`}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={3} mb={2}>
          <InfoBox
            value={Math.min(...chartData.map((d) => d.min))}
            unit="reps"
            color={theme.palette.text.secondary}
            label="Min Reps"
          />
          <InfoBox
            value={Math.max(...chartData.map((d) => d.max))}
            unit="reps"
            color={theme.palette.primary.main}
            label="Max Reps"
          />
        </Box>

        <Box position="relative" width="100%" height={308}>
          <ResponsiveContainer
            style={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              fontSize: 12,
            }}
            width="100%"
            height="100%"
          >
            <LineChart data={chartData} margin={{ right: 50, top: 10 }}>
              <CartesianGrid strokeWidth={1.5} strokeDasharray="3 3" />

              <XAxis
                dataKey="x"
                tickFormatter={(value) => dayjs(value).format("MMM D")}
                stroke={theme.palette.text.secondary}
                strokeWidth={1.5}
                tickMargin={10}
                minTickGap={15}
                //always show start date, added to be consistent with other charts
                interval="preserveStart"
              />

              <YAxis
                domain={[chartMin, chartMax]}
                ticks={chartTicks}
                stroke={theme.palette.text.secondary}
                strokeWidth={1.5}
                axisLine={false}
                tickLine={false}
              />

              <RechartsTooltip
                animationDuration={300}
                animationEasing="linear"
                cursor={false}
                content={ChartTooltip({
                  maxColor: theme.palette.primary.main,
                  minColor: theme.palette.text.secondary,
                  unitLabel: "reps",
                })}
              />

              <Line
                type="linear"
                dataKey="min"
                stroke={theme.palette.text.secondary}
                activeDot={{ r: 8 }}
                dot={{ r: 4 }}
                strokeWidth={2}
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                name="Min Reps"
              />

              <Line
                type="linear"
                dataKey="max"
                stroke={theme.palette.primary.main}
                dot={{ r: 4 }}
                strokeWidth={2}
                isAnimationActive
                animationDuration={500}
                animationEasing="ease-out"
                name="Max Reps"
              />
            </LineChart>
          </ResponsiveContainer>

          {chartData.length === 0 && <NoActivityExerciseMessageBox />}
        </Box>
      </Box>
    </ChartCard>
  );
};

export default RepsChart;
